import { Icon } from '../icon'
import { Combobox, Listbox, Transition } from '@headlessui/react'
import {
  StyledSelect,
  StyledSelectTrigger,
  StyledSelectTriggerDropdown,
  StyledNonSearchable,
  StyledSearchable,
  StyledSelectList,
  StyledSelectOptions,
} from './components'
import { MultiSelectProps } from './multi-select.types'
import { useMultiSelect } from './hooks/useMultiSelect'
import { useRef, useState } from 'react'
import { useClickOutside } from '../../util/useClickOutside'
import { SelectOption } from './option.types'

const formatSelectedItemsText = (
  value: Array<SelectOption> | undefined,
  placeholder?: string,
  shortPreviewText?: string
): string => {
  if (!value || value.length === 0) {
    return placeholder || ''
  }

  if (shortPreviewText && value.length > 1) {
    return shortPreviewText
  }

  return value.map((v) => v.label).join(', ')
}

const SelectBody = ({
  selectOptions,
  selectProps,
  disabled,
}: {
  selectOptions: ReturnType<typeof useMultiSelect>
  selectProps: MultiSelectProps
  disabled: boolean
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { renderElements, value, triggerProps } = selectOptions
  const {
    placeholder,
    status = 'default',
    orientation = 'down',
    shortPreviewText,
  } = selectProps

  const clickRef = useRef(null)
  useClickOutside(clickRef, () => setIsOpen(false))

  const isPlaceholder = !value || value.length === 0

  const displayText = formatSelectedItemsText(
    value,
    placeholder,
    shortPreviewText
  )

  return (
    <StyledSelect ref={clickRef}>
      <Listbox.Button
        style={{
          background: 'none',
          border: 'none',
          width: '100%',
          padding: '0',
          margin: '0',
        }}
        disabled={disabled}
        data-testid="toggle-multi-select"
        onClick={() => setIsOpen(true)}
      >
        <StyledSelectTrigger
          {...triggerProps}
          $disabled={disabled}
          $status={status}
        >
          <StyledNonSearchable
            $disabled={disabled}
            $isPlaceholder={isPlaceholder}
          >
            {displayText}
          </StyledNonSearchable>

          <StyledSelectTriggerDropdown
            style={{
              transform: isOpen ? 'rotate(-180deg)' : '',
            }}
          >
            <Icon name="dropdown-arrow-down" size={8} color="gray600" />
          </StyledSelectTriggerDropdown>
          <StyledSelectList $orientation={orientation}>
            {isOpen && (
              <StyledSelectOptions>
                <Listbox.Options
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                  static
                >
                  {renderElements}
                </Listbox.Options>
              </StyledSelectOptions>
            )}
          </StyledSelectList>
        </StyledSelectTrigger>
      </Listbox.Button>
    </StyledSelect>
  )
}

const SearchableSelectBody = ({
  open,
  selectOptions,
  selectProps,
  disabled,
}: {
  open: boolean
  selectOptions: ReturnType<typeof useMultiSelect>
  selectProps: MultiSelectProps
  disabled: boolean
}) => {
  const { renderElements, value, triggerProps, inputProps, afterLeave } =
    selectOptions
  const {
    placeholder,
    status = 'default',
    orientation = 'down',
    shortPreviewText,
    'data-testid': dataTestId,
  } = selectProps

  const displayText = formatSelectedItemsText(
    value,
    placeholder,
    shortPreviewText
  )
  const hasSelection = !!value && value.length > 0

  return (
    <StyledSelect>
      <Combobox.Button
        style={{
          background: 'none',
          border: 'none',
          width: '100%',
          padding: '0',
          margin: '0',
          outlineColor: 'black',
        }}
        disabled={disabled}
        data-testid="toggle-multi-select"
      >
        <StyledSelectTrigger
          {...triggerProps}
          $disabled={disabled}
          $status={status}
        >
          <StyledSearchable $disabled={disabled} $hasSelection={hasSelection}>
            <Combobox.Input
              {...inputProps}
              // The input only ever holds the query; the selection shows as its placeholder.
              displayValue={() => ''}
              placeholder={displayText}
              aria-label={placeholder}
              data-testid={
                dataTestId ? `${dataTestId}-input` : 'multi-select-input'
              }
            />
          </StyledSearchable>

          <StyledSelectTriggerDropdown
            style={{
              pointerEvents: disabled ? 'none' : 'auto',
              transform: open ? 'rotate(-180deg)' : '',
            }}
          >
            <Icon name="dropdown-arrow-down" size={8} color="gray600" />
          </StyledSelectTriggerDropdown>
        </StyledSelectTrigger>
      </Combobox.Button>

      {/* Outside the button: nesting the list in it would toggle the dropdown on every pick. */}
      <Transition show={open} afterLeave={afterLeave}>
        <StyledSelectList $orientation={orientation}>
          {open && (
            <StyledSelectOptions>
              <Combobox.Options
                style={{
                  margin: 0,
                  padding: 0,
                }}
                static
              >
                {renderElements}
              </Combobox.Options>
            </StyledSelectOptions>
          )}
        </StyledSelectList>
      </Transition>
    </StyledSelect>
  )
}

export const MultiSelect: React.FC<MultiSelectProps> = ({ ...props }) => {
  const {
    placeholder,
    name,
    options,
    onChange,
    defaultValue,
    disabled,
    noResultsFoundText,
    allowClear,
    searchable = false,
  } = props

  const select = useMultiSelect({
    options,
    searchable,
    placeholder,
    defaultValue,
    noResultsFoundText,
    allowClear,
  })

  const handleChange = (newValue: SelectOption[]) => {
    select.setValue(newValue)

    if (onChange) {
      onChange(newValue)
    }
  }

  return searchable ? (
    <Combobox
      name={name}
      value={select.value}
      onChange={handleChange}
      disabled={disabled}
      multiple
    >
      {({ open, disabled }) => (
        <SearchableSelectBody
          open={open}
          selectOptions={select}
          selectProps={props}
          disabled={disabled}
        />
      )}
    </Combobox>
  ) : (
    <Listbox
      name={name}
      value={select.value}
      defaultValue={defaultValue}
      onChange={handleChange}
      disabled={disabled}
      multiple
    >
      {({ disabled }) => (
        <SelectBody
          selectOptions={select}
          selectProps={props}
          disabled={disabled}
        />
      )}
    </Listbox>
  )
}
